import type { CompensationComputedView, CompensationDraftRequest } from '@/types/compensation';

import { usePreviewCompensationEnvelopeQuery } from '@/api/compensation';
import { useDebounce } from '@/hooks/useDebounce';

/** Long enough that dragging a slider fires one preview request instead of thirty, short enough
 * that the table feels live. */
export const COMPENSATION_PREVIEW_DEBOUNCE_MS = 400;

export interface UseCompensationPreviewParams {
    envelopeId: string | undefined;
    /** The draft to preview, or `undefined` while it still matches the saved envelope — nothing to
     * recompute then, since `savedView` already is that computation. */
    draftRequest: CompensationDraftRequest | undefined;
    /** The saved envelope's own computed view, shown until a preview of the current draft lands. */
    savedView: CompensationComputedView | undefined;
}

export interface UseCompensationPreviewResult {
    /** The single source of every number on screen: the server's computation of either the draft
     * or, while there is nothing to preview, the saved envelope. */
    computed: CompensationComputedView | undefined;
    /** The last preview of the current draft came back in error: the figures on screen are then
     * those of an earlier state, and the reader has to be told rather than left to trust them. */
    hasPreviewFailed: boolean;
}

/**
 * Keeps the report's figures in step with the draft without recomputing anything locally: the
 * draft is debounced, sent to `POST /{id}/Preview`, and the response replaces the saved envelope's
 * computed view.
 */
export const useCompensationPreview = ({
    envelopeId,
    draftRequest,
    savedView,
}: UseCompensationPreviewParams): UseCompensationPreviewResult => {
    const debouncedDraft = useDebounce(draftRequest, COMPENSATION_PREVIEW_DEBOUNCE_MS);

    const { data, isError } = usePreviewCompensationEnvelopeQuery({
        envelopeId,
        draft: debouncedDraft,
    });

    // `keepPreviousData` on the query keeps the last preview on screen while the next one loads;
    // dropping back to `savedView` the moment the draft stops differing is this hook's job, since
    // a stale preview of an abandoned draft would otherwise outlive it.
    const computed = draftRequest && data ? data : savedView;

    return { computed, hasPreviewFailed: !!draftRequest && isError };
};
